import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";

export type SupportedLanguage = "no" | "en";

type TranslationKey = keyof typeof translations;
type TranslationParams = Record<string, string | number>;

const LANGUAGE_STORAGE_KEY = "mcapps-language";

export const languageOptions: Array<{ value: SupportedLanguage; label: string }> = [
  { value: "no", label: "NO" },
  { value: "en", label: "EN" },
];

export const languageLocaleMap: Record<SupportedLanguage, string> = {
  no: "nb-NO",
  en: "en-GB",
};

const translations = {
  "app.projectPlaceholder": {
    no: "Velg prosjekt",
    en: "Choose project",
  },
  "app.invalidProjectId": {
    no: "Ugyldig prosjektId i URL",
    en: "Invalid projectId in URL",
  },
  "app.loadingProjectInfo": {
    no: "Laster inn prosjektinformasjon ...",
    en: "Loading project information ...",
  },
  "app.projectInfoError": {
    no: "Feil ved henting av prosjektinformasjon for prosjektNr {{projectId}}: {{error}}",
    en: "Error fetching project information for project no. {{projectId}}: {{error}}",
  },
  "app.terminated": {
    no: "Din bruker er midlertidig deaktivert. Vennligst prøv igjen senere eller kontakt support på mcapps@multiconsult.no for mer informasjon.",
    en: "Your user has been temporarily disabled. Please try again later or contact support at mcapps@multiconsult.no for more information.",
  },
  "common.cancel": {
    no: "Avbryt",
    en: "Cancel",
  },
  "common.close": {
    no: "Lukk",
    en: "Close",
  },
  "common.contact": {
    no: "Kontakt",
    en: "Contact",
  },
  "common.error": {
    no: "Feil!",
    en: "Error!",
  },
  "common.help": {
    no: "Få hjelp",
    en: "Get help",
  },
  "common.settings": {
    no: "Innstillinger",
    en: "Settings",
  },
  "common.success": {
    no: "Utført!",
    en: "Done!",
  },
  "common.technicalError": {
    no: "Se teknisk feilmelding",
    en: "Show technical error",
  },
  "common.userSettings": {
    no: "Brukerinnstillinger",
    en: "User settings",
  },
  "common.language": {
    no: "Språk",
    en: "Language",
  },
  "dev.resetAppState": {
    no: "Nullstill app state",
    en: "Reset app state",
  },
  "dev.testEnvironment": {
    no: "TESTMILJØ",
    en: "TEST ENVIRONMENT",
  },
  "feedback.error": {
    no: "Noe gikk galt! Prøv å sende en e-post i stedet.",
    en: "Something went wrong! Try sending an email instead.",
  },
  "feedback.success": {
    no: "Takk for tilbakemeldingen.\nVi vil komme tilbake til deg så snart vi kan.",
    en: "Thanks for the feedback.\nWe will get back to you as soon as we can.",
  },
  "noAccess.project": {
    no: "Du har ikke tilgang til prosjektet i Maconomy. Kontakt oppdragsansvarlig for å bli lagt til. Spørsmål? mcapps@multiconsult.no",
    en: "You do not have access to the project in Maconomy. Contact the project manager to be added. Questions? mcapps@multiconsult.no",
  },
  "notifications.allStatuses": {
    no: "Alle statuser",
    en: "All statuses",
  },
  "notifications.hide": {
    no: "Skjul notifikasjoner",
    en: "Hide notifications",
  },
  "notifications.markAllRead": {
    no: "Sett alle notifikasjoner som lest",
    en: "Mark all notifications as read",
  },
  "notifications.show": {
    no: "Vis notifikasjoner",
    en: "Show notifications",
  },
  "notifications.toggleRead": {
    no: "Vis / skjul leste meldinger",
    en: "Show / hide read messages",
  },
  "support.contactSupport": {
    no: "Kontakt support",
    en: "Contact support",
  },
  "tasks.goodEvening": {
    no: "God kveld",
    en: "Good evening",
  },
  "tasks.goodMorning": {
    no: "God morgen",
    en: "Good morning",
  },
  "tasks.goodAfternoon": {
    no: "God ettermiddag",
    en: "Good afternoon",
  },
  "tasks.hello": {
    no: "Hei",
    en: "Hi",
  },
  "tasks.mine": {
    no: "Mine oppgaver",
    en: "My tasks",
  },
  "tasks.overdueMessage": {
    no: "{{greeting}}, {{userName}}! Du har {{count}} {{taskLabel}} som har passert tidsfrist.",
    en: "{{greeting}}, {{userName}}! You have {{count}} overdue {{taskLabel}}.",
  },
  "tasks.overduePrefix": {
    no: "{{greeting}}, {{userName}}! Du har",
    en: "{{greeting}}, {{userName}}! You have",
  },
  "tasks.overdueSuffix": {
    no: "som har passert tidsfrist.",
    en: "overdue.",
  },
  "tasks.taskSingular": {
    no: "oppgave",
    en: "task",
  },
  "tasks.taskPlural": {
    no: "oppgaver",
    en: "tasks",
  },
  "user.logout": {
    no: "Logg ut",
    en: "Log out",
  },
  "error.dataFetchFailed": {
    no: "Datainnhenting mislyktes. Ta kontakt med mcapps@multiconsult.no for mer informasjon.",
    en: "Data fetch failed. Contact mcapps@multiconsult.no for more information.",
  },
} satisfies Record<string, Record<SupportedLanguage, string>>;

type LanguageContextValue = {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  languageSelectionEnabled: boolean;
  t: (key: TranslationKey, params?: TranslationParams) => string;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

const isSupportedLanguage = (value: string | null): value is SupportedLanguage =>
  value === "no" || value === "en";

const getInitialLanguage = (): SupportedLanguage => {
  return "no";
};

const interpolate = (text: string, params?: TranslationParams) => {
  if (params == null) {
    return text;
  }

  return Object.entries(params).reduce(
    (currentText, [key, value]) => currentText.replaceAll(`{{${key}}}`, String(value)),
    text,
  );
};

export const LanguageProvider: React.FC<PropsWithChildren<{ languageSelectionEnabled: boolean | null }>> = ({
  children,
  languageSelectionEnabled,
}) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(getInitialLanguage);

  const setLanguage = (nextLanguage: SupportedLanguage) => {
    if (languageSelectionEnabled !== true) {
      return;
    }

    localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    setLanguageState(nextLanguage);
  };

  useEffect(() => {
    if (languageSelectionEnabled === true) {
      const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (isSupportedLanguage(storedLanguage)) {
        setLanguageState(storedLanguage);
      }
      return;
    }

    setLanguageState("no");

    if (languageSelectionEnabled === false) {
      localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    }
  }, [languageSelectionEnabled]);

  useEffect(() => {
    document.documentElement.lang = languageLocaleMap[language];
  }, [language]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage,
      languageSelectionEnabled: languageSelectionEnabled === true,
      t: (key, params) => interpolate(translations[key]?.[language] ?? translations[key]?.no ?? key, params),
    }),
    [language, languageSelectionEnabled],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context == null) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }

  return context;
};

export const useTranslation = () => useLanguage().t;
